import React from "react";
import { Helmet } from "react-helmet-async";
import { useLanguage, buildLocalePath } from "@/context/LanguageContext";
import { LOCALES, DEFAULT_LOCALE } from "@/i18n/locales";

const SITE = "Marbella Royal Villas";
const ORIGIN = (typeof window !== "undefined" && window.location.origin) || "";

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE,
  url: ORIGIN,
  logo: `${ORIGIN}/logo.png`,
  address: {
    "@type": "PostalAddress",
    streetAddress: "Golden Mile",
    addressLocality: "Marbella",
    addressRegion: "Andalusia",
    postalCode: "29602",
    addressCountry: "ES",
  },
  sameAs: ["https://instagram.com", "https://facebook.com"],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE,
  url: ORIGIN,
  potentialAction: {
    "@type": "SearchAction",
    target: `${ORIGIN}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const faqSchema = (faq = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
});

export const breadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${ORIGIN}${it.url}`,
  })),
});

export const villaSchema = (villa) => ({
  "@context": "https://schema.org",
  "@type": "VacationRental",
  name: villa.name,
  description: villa.short_description,
  image: (villa.images || []).map((i) => (i.url && i.url.startsWith("/") ? `${ORIGIN}${i.url}` : i.url)).slice(0, 5),
  numberOfRooms: villa.bedrooms,
  occupancy: { "@type": "QuantitativeValue", maxValue: villa.max_guests },
  address: {
    "@type": "PostalAddress",
    addressLocality: villa.area,
    addressRegion: "Marbella",
    addressCountry: "ES",
  },
  aggregateRating: villa.reviews_count
    ? { "@type": "AggregateRating", ratingValue: villa.rating, reviewCount: villa.reviews_count }
    : undefined,
  priceRange: `EUR ${villa.price_per_night}/night`,
});

const Seo = ({ title, description, canonical, image, schema, noindex }) => {
  const { locale } = useLanguage();
  const fullTitle = title ? `${title} | ${SITE}` : `${SITE} | Premium Villa Rentals on the Costa del Sol`;
  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
  const localizedCanonical = canonical ? buildLocalePath(canonical, locale) : null;
  const showAlternates = canonical && !noindex;
  return (
    <Helmet>
      <html lang={locale} />
      <title>{fullTitle}</title>
      <meta name="description" content={description || "Rent the finest luxury villas in Marbella with private pools, sea views and concierge service."} />
      {localizedCanonical && <link rel="canonical" href={`${ORIGIN}${localizedCanonical}`} />}
      {showAlternates && LOCALES.map((l) => (
        <link key={l.code} rel="alternate" hrefLang={l.code} href={`${ORIGIN}${buildLocalePath(canonical, l.code)}`} />
      ))}
      {showAlternates && <link rel="alternate" hrefLang="x-default" href={`${ORIGIN}${buildLocalePath(canonical, DEFAULT_LOCALE)}`} />}
      {noindex && <meta name="robots" content="noindex" />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || ""} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE} />
      <meta property="og:locale" content={locale} />
      {localizedCanonical && <meta property="og:url" content={`${ORIGIN}${localizedCanonical}`} />}
      {image && <meta property="og:image" content={image.startsWith("/") ? `${ORIGIN}${image}` : image} />}
      {image && <meta property="og:image:alt" content={title || SITE} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || ""} />
      {image && <meta name="twitter:image" content={image.startsWith("/") ? `${ORIGIN}${image}` : image} />}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};

export default Seo;
